// ─────────────────────────────────────────────────────────────────────────────
// TourLauncherModal — the popup opened by the Play icon in the top bar.
//
// Shows tour goals grouped by category (Trading / Analysis / Learning / Account).
// Picking one calls startTour(flowId), which kicks off TourRunner.
//
// Styled as a bottom sheet to match the existing AppInfoModal.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import * as Icons from 'lucide-react';
import { ChevronRight, X, PlayCircle, Lock } from 'lucide-react';
import { useTour } from './TourContext';
import { TOUR_FLOWS, TOUR_CATEGORIES } from './tourFlows';
import { usePermissions } from '../hooks/usePermissions';
import UpgradeModal from '../components/UpgradeModal';

export default function TourLauncherModal() {
  const { launcherOpen, closeLauncher, startTour } = useTour();
  const { meetsMinPlan } = usePermissions();
  const [visible, setVisible] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    if (launcherOpen) requestAnimationFrame(() => setVisible(true));
    else setVisible(false);
  }, [launcherOpen]);

  if (!launcherOpen) return null;

  const handleClose = () => {
    setVisible(false);
    setTimeout(closeLauncher, 300);
  };

  // Only categories that actually have flows defined.
  const categories = TOUR_CATEGORIES
    .map((c) => ({ ...c, flows: c.flows.filter((id) => TOUR_FLOWS[id]) }))
    .filter((c) => c.flows.length > 0);

  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: `rgba(0,0,0,${visible ? 0.72 : 0})`,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        transition: 'background 0.3s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480,
          borderRadius: '24px 24px 0 0',
          background: '#13141a',
          border: '1px solid rgba(255,255,255,0.1)',
          borderBottom: 'none',
          maxHeight: '85vh', overflowY: 'auto',
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.35s cubic-bezier(0.32,0.72,0,1)',
        }}
      >
        {/* Handle bar */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 8px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.18)' }} />
        </div>

        {/* Header */}
        <div style={{ padding: '8px 20px 16px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 11, flexShrink: 0,
            background: '#6366F120', border: '1px solid #6366F135',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <PlayCircle size={18} color="#6366F1" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: 18, fontWeight: 900, color: '#eceef5', letterSpacing: '-0.02em', marginBottom: 3 }}>
              Guided tours
            </h2>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.42)', lineHeight: 1.5 }}>
              Pick what you want to learn. We'll walk you through it, step by step.
            </p>
          </div>
          <button onClick={handleClose} aria-label="Close" style={{ padding: 4, flexShrink: 0 }}>
            <X size={18} color="rgba(255,255,255,0.4)" />
          </button>
        </div>

        {/* Categories */}
        <div style={{ padding: '0 16px 40px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {categories.map((cat) => (
            <div key={cat.id}>
              <p style={{
                fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em',
                color: 'rgba(255,255,255,0.35)', margin: '0 4px 8px',
              }}>
                {cat.label}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {cat.flows.map((id) => {
                  const flow = TOUR_FLOWS[id];
                  const Icon = (flow.icon && Icons[flow.icon]) || PlayCircle;
                  const locked = flow.premium && !meetsMinPlan('premium');
                  return (
                    <button
                      key={id}
                      onClick={() => locked ? setUpgradeOpen(true) : startTour(id)}
                      style={{
                        width: '100%', textAlign: 'left',
                        borderRadius: 14,
                        background: 'rgba(99,102,241,0.06)',
                        border: '1px solid rgba(99,102,241,0.18)',
                        padding: '13px 14px',
                        display: 'flex', alignItems: 'center', gap: 12,
                        cursor: 'pointer',
                        opacity: locked ? 0.7 : 1,
                        position: 'relative',
                      }}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                        background: '#6366F118', border: '1px solid #6366F130',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon size={15} color="#6366F1" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13.5, fontWeight: 700, color: '#eceef5', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {flow.label}
                          {locked && (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              width: 16, height: 16, borderRadius: '50%',
                              background: 'rgba(234,179,8,0.2)', flexShrink: 0,
                            }}>
                              <Lock size={9} color="#eab308" />
                            </span>
                          )}
                        </p>
                        {flow.description && (
                          <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.42)', lineHeight: 1.4 }}>
                            {flow.description}
                          </p>
                        )}
                      </div>
                      <ChevronRight size={16} color="rgba(255,255,255,0.3)" style={{ flexShrink: 0 }} />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      {upgradeOpen && (
        <UpgradeModal
          requiredPlan="premium"
          message="This guided tour covers a premium feature. Upgrade to take it."
          onClose={() => setUpgradeOpen(false)}
        />
      )}
    </div>
  );
}
